import React, { useState, useEffect } from 'react';
import { Modal as AntdModal } from 'antd';
import { Button } from 'components';
import Table from 'components/Table';
import toast from 'helper/toast';
import { getErrorMessageFromObj } from 'utils/object';
import RecurringRuleModal from './components/RecurringRuleModal';
import { useIndexData, useActions } from './selectorData';
import { recurringRuleColumns } from './utils';
import classes from './ScheduledSettingsTab.module.scss';

const ScheduledSettingsTab = ({ companyId, permission, isActive }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const canManageScheduledServices = !!permission?.isAdmin;

  const {
    recurringRules,
    recurringRulesLoading,
    deleteRecurringRuleLoading,
    createRecurringRuleLoading,
    updateRecurringRuleLoading,
    orderTypes,
  } = useIndexData();

  const {
    getRecurringRules,
    createRecurringRule,
    updateRecurringRule,
    deleteRecurringRule,
    getAllOrderTypesList,
  } = useActions();

  const reloadRules = () => {
    if (companyId) {
      getRecurringRules(companyId);
    }
  };

  useEffect(() => {
    if (isActive && companyId) {
      reloadRules();
      getAllOrderTypesList();
    }
    // eslint-disable-next-line
  }, [isActive, companyId]);

  const openCreateModal = () => {
    setEditRule(null);
    setIsModalOpen(true);
  };

  const openEditModal = rule => {
    setEditRule(rule);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditRule(null);
  };

  const handleModalSuccess = () => {
    reloadRules();
    toast.success(
      editRule
        ? 'Recurring rule updated successfully.'
        : 'Recurring rule created successfully.'
    );
  };

  const handleDelete = record => {
    AntdModal.confirm({
      title: 'Delete Recurring Rule',
      content: `Are you sure you want to delete this recurring rule? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        deleteRecurringRule(companyId, record.id, (res, err) => {
          if (res) {
            reloadRules();
            toast.success('Recurring rule deleted successfully.');
          } else {
            toast.error(getErrorMessageFromObj(err)[0]);
          }
        });
      },
    });
  };

  const columns = recurringRuleColumns({
    onEdit: openEditModal,
    onDelete: handleDelete,
    canManageScheduledServices,
  });

  const tableData = Array.isArray(recurringRules)
    ? recurringRules
    : recurringRules?.data || [];

  return (
    <div className={classes.scheduledSettingsTab}>
      <div className={classes.tabHeader}>
        <h3>Scheduled Annual Services</h3>
        {canManageScheduledServices && (
          <Button type="primary" onClick={openCreateModal}>
            Create New Rule
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        data={tableData}
        rowKey="id"
        loading={recurringRulesLoading || deleteRecurringRuleLoading}
        isScroll
        scroll={{ x: 800 }}
      />

      <RecurringRuleModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
        companyId={companyId}
        editRule={editRule}
        orderTypes={
          Array.isArray(orderTypes) ? orderTypes : orderTypes?.data || []
        }
        createLoading={createRecurringRuleLoading}
        updateLoading={updateRecurringRuleLoading}
        onCreateRule={createRecurringRule}
        onUpdateRule={updateRecurringRule}
      />
    </div>
  );
};

export default ScheduledSettingsTab;
